import type { SimpleUser } from '@/types/simpleUser';
import { queryClient } from '@/lib/queryClient';
import { queryKeys } from '@/lib/queryKeys';
import { getQueryClient } from '@/lib/queryClientHolder';
import type { ActiveOperator } from './auth.operator';
import {
  isSimpleOperator,
  operatorFromSimpleUser,
  simpleUserFromOperator,
} from './auth.operator';
import type { AuthStoreGet, AuthStoreSet } from './auth.types';

function resolveOrganizationSlug(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('currentOrganizationSlug');
}

export function invalidateOrganizationQueries(slug?: string | null): void {
  const orgSlug = slug ?? resolveOrganizationSlug();
  if (!orgSlug) return;

  queryClient.invalidateQueries({ queryKey: queryKeys.organization(orgSlug) });
}

export const createOperatorActions = (set: AuthStoreSet, get: AuthStoreGet) => ({
  setActiveOperator: (
    operator: ActiveOperator | null,
    options?: { simpleUser?: SimpleUser | null; organizationId?: string },
  ) => {
    const simpleUser = isSimpleOperator(operator)
      ? options?.simpleUser ??
        simpleUserFromOperator(operator, options?.organizationId)
      : null;

    set({ activeOperator: operator, simpleUser });
    invalidateOrganizationQueries();
  },

  applySimpleUserOperator: (simpleUser: SimpleUser) => {
    get().setActiveOperator(operatorFromSimpleUser(simpleUser), {
      simpleUser,
      organizationId: simpleUser.organization_id,
    });
  },

  applyMemberOperator: () => {
    get().setActiveOperator({ type: 'member' }, { simpleUser: null });
  },

  clearActiveOperator: () => {
    set({ activeOperator: null, simpleUser: null });
    invalidateOrganizationQueries();
  },
});
